import { useQuery } from '@tanstack/react-query';

import config from 'src/config';
import activeProjects from './aa.json';
import { API } from 'src/reactQuery/Api';
import { QUERY_KEYS } from 'src/reactQuery/QueryKeys';
import { dateSystemFormat, toString } from 'src/utils/datetime';
import { paginationMapper, postRequest } from 'src/utils/request';

import type { Moment } from 'moment';

const PAGE_SIZE = 35;

export type activeProjectParamsType = {
  page: number;
  periodTo: Moment;
  periodFrom: Moment;
  projectIds: string | null;
  projectName: string | null;
};

const convertProjectIds = (projectIds: string) => {
  return projectIds.split(',').map((id) => parseInt(id));
};

const mapActiveProjects = (projects: Array<UCM.ActiveProjectRawType>) => {
  return projects.map(({ projectId, ...other }) => ({
    ...other,
    id: projectId,
  }));
};

const getActiveProjects = async (params: activeProjectParamsType) => {
  if (config.useAutoAcceptMockData) {
    return {
      data: mapActiveProjects(activeProjects.projects),
      meta: paginationMapper({
        current_page: 1,
        total_data: activeProjects.projects.length,
        total_pages: Math.ceil(activeProjects.projects.length / PAGE_SIZE),
      }),
    };
  }

  const body = {
    rowLimit: PAGE_SIZE,
    rowOffset: (params.page - 1) * PAGE_SIZE,
    periodTo: toString(params.periodTo, dateSystemFormat),
    periodFrom: toString(params.periodFrom, dateSystemFormat),
    ...(params.projectIds && {
      projectIds: convertProjectIds(params.projectIds),
    }),
    ...(params.projectName && { projectNamePart: params.projectName }),
  };

  const { data } = await postRequest<{
    rowTotalCount: number;
    projects: Array<UCM.ActiveProjectRawType>;
  }>(`${API.GET.autoAcceptance}${API.POST.getActiveProjects}`, body);

  return {
    data: mapActiveProjects(data.projects),
    meta: paginationMapper({
      current_page: params.page,
      total_data: data.rowTotalCount,
      total_pages: Math.ceil(data.rowTotalCount / PAGE_SIZE),
    }),
  };
};

export const useGetActiveProjects = (
  params: activeProjectParamsType,
  config: { enabled?: boolean } = { enabled: true },
) => {
  return useQuery({
    queryKey: [
      QUERY_KEYS.AUTO_ACCEPTANCE_ACTIVE_PROJECTS,
      API.POST.getActiveProjects,
      params,
    ],
    queryFn: () => getActiveProjects(params),
    staleTime: 1000 * 60 * 30,
    ...config,
  });
};
